import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import { UnitRepository } from '~/repositories/unitRepository';
import type { UnitFilter, UnitImagePayload, UnitPaymentFilter } from '~/repositories/unitRepository';
import type { CreateUnitDto, HydratedUnitDto, UpdateUnitDto, UpdateUnitMagicTagDto } from '~/swagger/portal/Api';

const repo = new UnitRepository();

export const [UnitsProvider, useUnits] = createMagicDoorContext('Units', () => {
  const [units, getUnits, unitsActions] = createTriggerResource((filter: UnitFilter) => repo.getUnits({ active: true, ...filter }));

  const [unit, getUnit, unitActions] = createTriggerResource((unitId: string) => repo.getUnit(unitId));

  const refetchUnit = createMutation(async (unitId: string) => {
    const isCurrent = unit()?.id === unitId;
    const inList = units()?.items.some((u) => u.id === unitId);
    if (!isCurrent && !inList) return;

    const hydrated = await repo.getUnit(unitId);
    isCurrent && unitActions.mutate(hydrated);
    inList && unitsActions.mutate((prev) => prev && { ...prev, items: prev.items.map((i) => (i.id === unitId ? hydrated : i)) });
  });

  const [unitPayments, getUnitPayments, unitPaymentsActions] = createTriggerResource((filter?: UnitPaymentFilter) =>
    repo.getUnitPayments(filter)
  );

  const refetchUnitsIf = createMutation(async (condition?: (unit: HydratedUnitDto) => boolean | undefined) => {
    if (typeof condition !== 'function') return;
    const currentUnit = unit();
    if (currentUnit && condition(currentUnit)) {
      await unitActions.refetch();
    }
    const currentPagination = units();
    if (currentPagination && currentPagination.items.some(condition)) {
      await unitsActions.refetch();
    }
  });

  const addUnit = createMutation(async (payload: CreateUnitDto, refreshAfterAdded: boolean = true) => {
    const added = await repo.addUnit(payload);

    if (refreshAfterAdded) {
      unitsActions.refetch();
    }

    return added;
  });

  const updateUnit = createMutation(async (unitId: string, payload: UpdateUnitDto) => {
    const updated = await repo.updateUnit(unitId, payload);
    refetchUnit(unitId);
    return updated;
  });

  const updateUnitMagicTags = createMutation(async (unitId: string, magicTags: UpdateUnitMagicTagDto) => {
    const updated = await repo.updateUnitMagicTags(unitId, magicTags);
    refetchUnit(unitId);
    return updated;
  });

  /** Not preferred. use deactivateUnit instead in most cases */
  const deleteUnit = createMutation(async (unitId: string) => {
    await repo.deleteUnit(unitId);
    if (unitId === unit()?.id) unitActions.mutate(undefined);
    unitsActions.refetch();
  });

  const deactivateUnit = createMutation(async (unitId: string, forceEndLease?: boolean) => {
    await repo.deactivateUnit(unitId, forceEndLease);
    if (unitId === unit()?.id) unitActions.mutate((prev) => prev && { ...prev, active: false });
    unitsActions.refetch();
  });

  const addUnitImage = createMutation(async (unitId: string, payload: UnitImagePayload | UnitImagePayload[]) => {
    const images = Array.isArray(payload) ? payload : [payload];
    await Promise.all(images.map((image) => repo.addUnitImage(unitId, image)));
    return refetchUnit(unitId);
  });

  const batchAddUnitImages = createMutation(async (unitId: string, payload: UnitImagePayload[]) => {
    await Promise.all(payload.map((image) => repo.addUnitImage(unitId, image)));
  });

  const clearUnit = createMutation(async () => {
    unitActions.mutate(undefined);
  });

  return {
    units,
    getUnits,
    unit,
    getUnit,
    refetchUnit,
    refetchUnitsIf,
    addUnit,
    updateUnit,
    deleteUnit,
    deactivateUnit,
    addUnitImage,
    batchAddUnitImages,
    unitPayments,
    getUnitPayments,
    unitPaymentsActions,
    clearUnit,
    unitActions,
    updateUnitMagicTags,
  };
});
